import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { usePlatform } from "../context/PlatformContext";
import { useSeo } from "../lib/useSeo";
import { Heart, ShoppingCart, Gavel, Clock, Truck, Shield, MapPin, Eye, Zap, BellRing, Gift, Sparkles, Trophy, AlertCircle, CheckCircle2, MessageCircle, PencilLine } from "lucide-react";
import PromoStripBanner from "../components/PromoStripBanner";
import { toast } from "sonner";
import { motion } from "framer-motion";
import BidConfirmDialog from "../components/BidConfirmDialog";
import ReviewSection from "../components/ReviewSection";
import ClassificationChip from "../components/ClassificationChip";
import { StarRow } from "../components/ReviewSection";
import CollectibleChips from "../components/CollectibleChips";
import StrainAtlasWidget from "../components/listing/StrainAtlasWidget";
import ShareButton from "../components/ShareButton";
import AuctionPayButton from "../components/AuctionPayButton";
import MakeOfferModal from "../components/MakeOfferModal";
import { listingUrl } from "../lib/urls";
import { parseServerDate } from "../lib/serverDate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^http/, "ws");

// v3.99d.351 — Server-time source with lightweight skew correction.
// Runs one fetch at mount, then every 5 minutes, to compute the
// offset between the server clock and the browser clock. All
// auction-close comparisons and countdown ticks derive their "now"
// from this hook so a user's fast/slow browser clock cannot make an
// auction appear to end early or late.
function useServerTime() {
  const [offsetMs, setOffsetMs] = useState(0);
  const [ready, setReady] = useState(false);
  const measure = useCallback(async () => {
    try {
      const t0 = Date.now();
      const r = await api.get("/server-time");
      const t1 = Date.now();
      const rtt = (t1 - t0) / 2;
      const serverEpoch = Number(r?.data?.epoch_ms) || 0;
      if (!serverEpoch) return;
      // Estimated server "now" at the moment the response arrived:
      // serverEpoch was captured server-side ~rtt/2 ms ago.
      const estServerNow = serverEpoch + rtt;
      setOffsetMs(estServerNow - t1);
      setReady(true);
    } catch {
      // Silent failure — countdown falls back to browser clock,
      // which is the pre-v3.99d.351 behaviour.
    }
  }, []);
  useEffect(() => {
    measure();
    const id = setInterval(measure, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [measure]);
  const nowMs = useCallback(() => Date.now() + offsetMs, [offsetMs]);
  return { nowMs, offsetMs, ready, resync: measure };
}

// v3.99d.351 — Server-authoritative countdown.
// v3.99d.355 — Formats an auction close time in the collector's local
// timezone (using `Intl.DateTimeFormat`) so bidders see EXACTLY when
// the auction ends in their zone — no mental UTC math. The UTC line
// is kept below so admins and international collectors have a shared
// reference. Timezone abbreviation (e.g. "EST", "CET") comes from
// `timeZoneName: 'short'` which Chrome/Safari/Firefox all support.
function LocalCloseTimeBanner({ endsAt }) {
  if (!endsAt) return null;
  const dt = parseServerDate(endsAt);
  if (Number.isNaN(dt.getTime())) return null;
  let local = "";
  let tzAbbr = "";
  try {
    local = new Intl.DateTimeFormat(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).format(dt);
    // Extract the trailing tz label so we can style it.
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: "short",
    }).formatToParts(dt);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    tzAbbr = tzPart ? tzPart.value : "";
  } catch {
    local = dt.toLocaleString();
  }
  const utc = new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "UTC", hour12: false,
  }).format(dt);
  return (
    <div className="text-[11px] leading-snug mb-2"
         style={{color: "var(--text-muted)"}}
         data-testid="auction-close-banner"
         data-tz-abbr={tzAbbr}>
      <span data-testid="auction-close-local">Closes {local}</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span data-testid="auction-close-utc">{utc} UTC</span>
    </div>
  );
}

// v3.99d.355 — Subtle "final seconds" audio cue. Fires a single soft
// tick each second inside the last 5 seconds and a slightly warmer
// tone at zero. WebAudio has no plumbing cost until the tone plays,
// and modern browsers only allow it after user interaction — which
// is fine because reaching a listing page requires a click. Fails
// silently on browsers that block or lack support.
function playCountdownTick(context, kind) {
  try {
    const ctx = context.current || (context.current = new (window.AudioContext || window.webkitAudioContext)());
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    // Tick: short 880 Hz blip. Final: warmer 660 Hz for 220 ms.
    const isFinal = kind === "final";
    osc.frequency.value = isFinal ? 660 : 880;
    osc.type = "sine";
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t0 = ctx.currentTime;
    const dur = isFinal ? 0.22 : 0.06;
    // Very quiet by design — audible but not startling.
    gain.gain.exponentialRampToValueAtTime(isFinal ? 0.08 : 0.05, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  } catch {
    /* audio unavailable — silent no-op */
  }
}

// Uses skew-corrected server time. At zero we enter a brief
// "Confirming final result…" state and let the parent refetch the
// listing before flipping to AUCTION ENDED. The parent hands us the
// ``onZero`` callback which fires exactly once per endsAt.
function CountdownTimer({ endsAt, nowMs, onZero }) {
  const [tick, setTick] = useState(0);
  const zeroFiredRef = useRef(null);
  // v3.99d.355 — Audio cue state. `audioCtxRef` lazily instantiates
  // the AudioContext (some browsers create real resources on `new`).
  // `lastCueSecRef` de-dupes so we play at most one tick per second
  // even though React re-renders more often. `mutedRef` mirrors a
  // sessionStorage toggle so refreshing keeps the user's choice.
  const audioCtxRef = useRef(null);
  const lastCueSecRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    try { return sessionStorage.getItem("cf-countdown-muted") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Reset the once-per-endsAt latch whenever the parent hands us
    // a new close time (anti-snipe extension).
    zeroFiredRef.current = endsAt;
    lastCueSecRef.current = null;
  }, [endsAt]);
  const toggleMuted = () => {
    setMuted((m) => {
      const next = !m;
      try { sessionStorage.setItem("cf-countdown-muted", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  if (!endsAt) return null;
  const now = nowMs ? nowMs() : Date.now();
  const ms = parseServerDate(endsAt).getTime() - now;
  if (ms <= 0) {
    if (zeroFiredRef.current === endsAt && onZero) {
      zeroFiredRef.current = "fired:" + endsAt;
      setTimeout(onZero, 0);
      // Play the closing tone once. Guarded by the same latch so
      // an unmount/remount doesn't retrigger it.
      if (!muted) playCountdownTick(audioCtxRef, "final");
    }
    return (
      <span className="font-mono text-lg"
            style={{color:"var(--text-2)"}}
            data-testid="auction-countdown-confirming">
        Confirming final result…
      </span>
    );
  }
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const urgent = ms < 60000;
  // v3.99d.355 — Fire a soft tick at each of the last 5 seconds. We
  // key on `totalSec` so React re-renders inside the same second
  // don't stack ticks on top of each other. `lastCueSecRef` also
  // prevents double-play across the WebSocket-driven re-renders.
  if (!muted && totalSec > 0 && totalSec <= 5 && lastCueSecRef.current !== totalSec) {
    lastCueSecRef.current = totalSec;
    playCountdownTick(audioCtxRef, "tick");
  }
  return (
    <span className={`font-mono text-2xl tracking-tight ${urgent ? "pulse-warn" : ""}`}
          style={{color: urgent ? "var(--secondary)" : "var(--text)"}}
          data-testid="auction-countdown"
          data-remaining-ms={ms}>
      {d>0 && `${d}d `}{String(h).padStart(2,"0")}:{String(m).padStart(2,"0")}:{String(s).padStart(2,"0")}
      <button type="button"
              onClick={toggleMuted}
              aria-label={muted ? "Enable countdown sound" : "Mute countdown sound"}
              title={muted ? "Enable countdown sound" : "Mute countdown sound"}
              data-testid="auction-countdown-mute-toggle"
              data-muted={muted ? "true" : "false"}
              className="ml-2 opacity-50 hover:opacity-90 transition-opacity text-xs align-middle"
              style={{background: "transparent", border: "none", cursor: "pointer",
                      color: "var(--text-muted)"}}>
        {muted ? "🔇" : "🔈"}
      </button>
    </span>
  );
}

export default function ListingDetail() {
  // v3.99d.200 — Dual route support. The canonical URL is
  // /shop/:breederSlug/:productSlug; /listing/:id is the legacy UUID
  // route that resolves the listing then 301-replaces the URL with the
  // canonical slug-based path.
  const { id, breederSlug, productSlug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { preview_mode } = usePlatform();
  const [listing, setListing] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidAmount, setBidAmount] = useState("");
  // v3.99d.341y — dedicated bid-confirmation dialog state (replaces
  // window.confirm so the two-touch UX matches the Forge/quick-bid
  // surfaces and the confirm button shows the exact final amount).
  const [bidConfirmOpen, setBidConfirmOpen] = useState(false);
  const [bidPending, setBidPending] = useState(null);
  const [bidBusy, setBidBusy] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [presence, setPresence] = useState(0);
  const [bumpKey, setBumpKey] = useState(0);
  // v3.99d.251 — Watch button toggle state.
  const [watching, setWatching] = useState(false);
  // v3.99d.351 — Live-updates connection status + polling fallback
  // book-keeping. `wsStatus` is one of: "connecting" | "open" |
  // "reconnecting" | "closed". `lastServerUpdate` records when the
  // client last received authoritative data (WS msg or listing GET);
  // if it drifts stale we suppress "You won" / "Auction Ended"
  // terminal messaging.
  const [wsStatus, setWsStatus] = useState("connecting");
  const [lastServerUpdate, setLastServerUpdate] = useState(Date.now());
  const [confirmingResult, setConfirmingResult] = useState(false);
  const serverTime = useServerTime();
  const wsRetryRef = useRef(0);
  const wsReconnectTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  // v3.99d.300 Phase 1 — Offer Only modal + recent-offer state so the
  // CTA flips into "Offer submitted" after a successful submission.
  const [offerOpen, setOfferOpen] = useState(false);
  const [myActiveOffer, setMyActiveOffer] = useState(null);
  const [watchBusy, setWatchBusy] = useState(false);
  // v3.99d.272 — Per-user authoritative bid status for the auction
  // detail page. Drives the "You are the highest bidder" / "You have
  // been outbid" / win-state pill. Backed by GET
  // /api/listings/{id}/my-bid-status which never leaks another
  // bidder's identity.
  const [myBidStatus, setMyBidStatus] = useState(null);
  // v3.99d.232 — Cart-aware shipping hint. Per-listing `shipping_cost`
  // is NOT charged at checkout (cart uses a single flat rate with a
  // free-shipping threshold), so showing "$0" on the listing was
  // misleading customers. We fetch commerce settings once and render
  // the cart-level rule on the listing row instead.
  const [commerce, setCommerce] = useState(null);
  // v3.99d.234 — Live cart snapshot for the free-shipping progress bar
  // shown under the reassurance line. Only fetched for logged-in users
  // (the public /cart endpoint requires auth). Silently absent for
  // anonymous shoppers — they still get the static reassurance line.
  const [cart, setCart] = useState(null);
  useEffect(() => {
    api.get("/commerce/settings").then(r => setCommerce(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) { setCart(null); return; }
    api.get("/cart").then(r => setCart(r.data)).catch(() => setCart(null));
  }, [user]);
  const wsRef = useRef(null);

  // Canonical URL is always the slug-based form once we know the
  // listing. Falls back to /listing/:id only when slugs are missing.
  const canonicalPath = listing && listing.store_slug && listing.product_slug
    ? `/shop/${listing.store_slug}/${listing.product_slug}`
    : (listing ? listingUrl(listing) : `/listing/${id || ""}`);

  useSeo({
    title: listing
      ? `${listing.title} by ${listing.store_name}${listing.lineage ? ` | ${listing.lineage}` : ""} | CannaForge`
      : "Listing — CannaForge",
    description: listing
      ? `${listing.title} by ${listing.store_name}${listing.lineage ? ` is a ${listing.lineage} genetics release` : ""} listed on CannaForge, the curated platform for verified breeders, strain records, and collector genetics.`
      : "Listing on CannaForge — premium cannabis genetics marketplace.",
    canonical: canonicalPath,
    ogImage: listing?.images?.[0] || "https://thecannaforge.com/og-cover.jpg",
    ogType: "product",
    // v3.99bq — Product / Offer JSON-LD for rich Google search results.
    jsonLd: listing ? [{
      "@context": "https://schema.org",
      "@type": "Product",
      "name": listing.title || listing.strain_name,
      "description": (listing.description || `${listing.title} by ${listing.store_name}`).slice(0, 500),
      "image": listing.images || [],
      "brand": { "@type": "Brand", "name": listing.store_name || "CannaForge" },
      "sku": listing.id,
      "category": listing.seed_type || listing.category || "Cannabis Seeds",
      "url": `https://thecannaforge.com${canonicalPath}`,
      ...(listing.listing_type === "buy_now" && listing.price ? {
        "offers": {
          "@type": "Offer",
          "url": `https://thecannaforge.com${canonicalPath}`,
          "priceCurrency": "USD",
          "price": listing.price,
          "availability": (listing.quantity || 0) > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        },
      } : {}),
      ...(listing.rating && listing.review_count ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": listing.rating,
          "reviewCount": listing.review_count,
        },
      } : {}),
    },
    // v3.99d.230 — BreadcrumbList JSON-LD: Home > Shop > Breeder > Product.
    // Uses the canonical clean URLs at every level — never UUIDs.
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://thecannaforge.com/" },
        { "@type": "ListItem", position: 2, name: "Shop", item: "https://thecannaforge.com/shop" },
        ...(listing.store_slug ? [{
          "@type": "ListItem", position: 3,
          name: listing.store_name || listing.store_slug,
          item: `https://thecannaforge.com/shop/${listing.store_slug}`,
        }] : []),
        {
          "@type": "ListItem",
          position: listing.store_slug ? 4 : 3,
          name: listing.title || listing.strain_name,
          item: `https://thecannaforge.com${canonicalPath}`,
        },
      ],
    }] : null,
  });

  const load = async () => {
    let data;
    if (breederSlug && productSlug) {
      // Canonical slug route — resolve directly.
      const r = await api.get(
        `/listings/by-slug/${encodeURIComponent(breederSlug)}/${encodeURIComponent(productSlug)}`,
      );
      data = r.data;
    } else if (id) {
      const r = await api.get(`/listings/${id}`);
      data = r.data;
      // v3.99d.200 — UUID URL hit. Replace the browser URL with the
      // canonical slug-based path so users / refresh / share-target
      // the clean URL going forward. This is the client fallback for
      // the SSR 301 in seo_render.py.
      if (data?.store_slug && data?.product_slug) {
        nav(`/shop/${data.store_slug}/${data.product_slug}`,
            { replace: true });
      }
    }
    if (!data) return;
    setListing(data);
    if (data.listing_type === "auction") {
      const b = await api.get(`/listings/${data.id}/bids`);
      setBids(b.data);
    }
    // v3.99d.251 — Hydrate watch state once we know the listing id,
    // so the Watch button can render as "Watching" / "Remove from
    // Watchlist" on first paint (no flicker).
    if (data?.id && user) {
      try {
        const r = await api.get(`/watchlist/check/${data.id}`);
        setWatching(!!r.data?.watching);
      } catch { /* anon or transient — leave default */ }
    }
    // v3.99d.272 — Per-user bid status for auction pages. Anonymous
    // users skip this entirely; non-auctions ignore the value.
    if (data?.id && user && data.listing_type === "auction") {
      try {
        const s = await api.get(`/listings/${data.id}/my-bid-status`);
        setMyBidStatus(s.data || null);
      } catch { setMyBidStatus(null); }
    } else {
      setMyBidStatus(null);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, breederSlug, productSlug]);

  // v3.99d.200 — All POST/WS calls below use the resolved listing's
  // canonical UUID (`listing.id`) rather than the URL param `id`,
  // because the slug-route variant doesn't have a UUID param.
  const lid = listing?.id || id;

  // v3.99d.351 — Robust WebSocket + polling fallback.
  //
  // The previous implementation set only `onmessage` and never
  // reconnected on drop, so mobile tab sleeps / cellular flakiness
  // silently froze the customer's view of `current_bid` and
  // `auction_ends_at`. This effect now:
  //   * exposes wsStatus ("connecting" | "open" | "reconnecting"
  //     | "closed") so the UI can render a "Live updates
  //     disconnected — refreshing" banner
  //   * reconnects with bounded exponential backoff (1s → 30s cap)
  //   * runs a 5s polling fallback whenever the socket is not open
  //     so stale bids / extensions are still recovered
  //   * updates `lastServerUpdate` on every authoritative refresh
  //     so terminal UI ("Auction Ended", "You won") can suppress
  //     itself when data is stale.
  const applyFreshListing = useCallback((fresh) => {
    if (!fresh || !fresh.id) return;
    setListing(prev => prev ? { ...prev, ...fresh } : fresh);
    setLastServerUpdate(Date.now());
  }, []);

  const pollNow = useCallback(async () => {
    if (!lid) return;
    try {
      const r = await api.get(`/listings/${lid}`);
      applyFreshListing(r.data);
    } catch { /* transient; next tick will retry */ }
  }, [lid, applyFreshListing]);

  useEffect(() => {
    if (!listing || listing.listing_type !== "auction") return;
    let closedByUnmount = false;
    let ws;
    const connect = () => {
      setWsStatus(prev => (prev === "open" ? prev : "connecting"));
      try {
        ws = new WebSocket(`${WS_URL}/api/ws/auction/${lid}`);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        wsRetryRef.current = 0;
        setWsStatus("open");
        // Stop the fallback poller — the socket is authoritative
        // again.
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        // One-shot reconcile so any missed events during the
        // disconnect window are recovered.
        pollNow();
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "presence") setPresence(msg.count);
          if (msg.type === "bid") {
            setListing(prev => prev ? {
              ...prev,
              current_bid: msg.current_bid,
              bid_count: msg.bid_count,
              auction_ends_at: msg.auction_ends_at,
              high_bidder_id: msg.high_bidder_id ?? prev.high_bidder_id,
            } : prev);
            setBids(prev => [msg.bid, ...prev]);
            setBumpKey(k => k + 1);
            setLastServerUpdate(Date.now());
            // If the bid extended the timer, cancel any in-flight
            // "Confirming final result" state so the countdown
            // resumes cleanly.
            if (msg.extended) setConfirmingResult(false);
            if (user) {
              api.get(`/listings/${lid}/my-bid-status`)
                .then(r => setMyBidStatus(r.data || null))
                .catch(() => {});
            }
          }
          // v3.99d.376 — Admin corrected a mistyped top bid. Same top
          // bidder stays in the lead; only the amount + first bid row
          // in the panel change.
          if (msg.type === "bid_corrected") {
            setListing(prev => prev ? {
              ...prev,
              current_bid: msg.current_bid,
              bid_count: msg.bid_count,
              high_bidder_id: msg.high_bidder_id ?? prev.high_bidder_id,
              auction_ends_at: msg.auction_ends_at ?? prev.auction_ends_at,
            } : prev);
            setBids(prev => {
              if (!prev || prev.length === 0) return prev;
              // Patch the top bid row in the panel — bids are sorted
              // newest-first, and the top bidder's newest bid is what
              // the admin rewrote.
              const [head, ...rest] = prev;
              return [{ ...head, amount: msg.current_bid }, ...rest];
            });
            setBumpKey(k => k + 1);
            setLastServerUpdate(Date.now());
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onerror = () => {
        // Let onclose handle backoff — some browsers fire only one.
        setWsStatus("reconnecting");
      };
      ws.onclose = () => {
        if (closedByUnmount) return;
        setWsStatus("reconnecting");
        startPollingFallback();
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (closedByUnmount) return;
      const attempt = wsRetryRef.current++;
      const delay = Math.min(30000, 1000 * Math.pow(2, attempt));
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
      }
      wsReconnectTimerRef.current = setTimeout(connect, delay);
    };

    const startPollingFallback = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(pollNow, 5000);
    };

    connect();

    return () => {
      closedByUnmount = true;
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      try { ws && ws.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, listing?.listing_type, id]);

  // v3.99d.351 — Reconcile on visibility change. If the tab was
  // hidden for a long time the WebSocket may have been torn down
  // silently by the browser; force a refetch so we never resume
  // showing a stale current_bid or auction_ends_at.
  useEffect(() => {
    if (!listing || listing.listing_type !== "auction") return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      pollNow();
      // Also refresh personal bid status (existing behaviour).
      if (user) {
        api.get(`/listings/${lid}/my-bid-status`)
          .then(r => setMyBidStatus(r.data || null))
          .catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, listing?.listing_type, user?.id, lid]);

  // v3.99d.351 — Countdown-hit-zero callback. Instead of the browser
  // clock flipping to "AUCTION ENDED", we enter the confirming
  // state and refetch the authoritative listing. If ends_at was
  // extended the countdown resumes; otherwise the settled listing
  // will report auction_outcome and the parent component's post-
  // settlement UI takes over.
  const onCountdownZero = useCallback(() => {
    setConfirmingResult(true);
    pollNow().finally(() => {
      // Give the settlement loop a moment; if still no outcome +
      // no extension we clear the spinner so the ended UI shows.
      setTimeout(() => setConfirmingResult(false), 4000);
    });
  }, [pollNow]);

  // v3.99d.272 — Personal bid status is now refreshed inside the
  // v3.99d.351 visibilitychange handler above (which ALSO reconciles
  // the listing itself). The old per-user-only effect is removed
  // so we never present stale current_bid / auction_ends_at.

  // v3.99d.300 Phase 1 — Load this user's existing offer on this
  // listing (if any) so the CTA can show its live status. Skips when
  // the listing isn't in offer_only mode.
  useEffect(() => {
    const isOO = listing?.listing_type === "offer_only";
    if (!isOO || !user || !listing?.id) return;
    let alive = true;
    api.get("/users/me/offers")
      .then((r) => {
        if (!alive) return;
        const mine = (r.data?.items || []).find(
          (o) => o.listing_id === listing.id
                  && ["submitted", "under_review", "countered",
                      "accepted_awaiting_payment"].includes(o.status));
        setMyActiveOffer(mine || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [listing?.listing_type, user, listing?.id]);

  if (!listing) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">Loading…</div>;

  const isAuction = listing.listing_type === "auction";
  const isOfferOnly = listing.listing_type === "offer_only";
  // v3.99d.341y — prefer the server-authoritative next_min_bid on
  // the listing payload. Falls back to a $1 client calc for older
  // cached responses. Never applies a 5% escalation any more so
  // the amount shown always matches what the backend will accept.
  const minNext = (() => {
    const nxt = Number(listing.next_min_bid);
    if (Number.isFinite(nxt) && nxt > 0) return nxt.toFixed(2);
    const inc = Number(listing.min_bid_increment) || 1;
    return ((Number(listing.current_bid)
             || Number(listing.starting_bid) || 0) + inc).toFixed(2);
  })();
  // v3.99d.351 — auction-ended gate now uses server-corrected time
  // AND the authoritative auction_outcome. A user with a fast local
  // clock can no longer see the bid form disabled early. And a
  // stale connection (no fresh data in >20s while the WS is
  // reconnecting) suppresses any terminal messaging so we don't
  // present a confirmed winner/ended state against unverified data.
  // v3.99d.356 — HZTGS incident: additionally parse the timestamp
  // via `parseServerDate` so a legacy naive ISO in the response can't
  // trick the browser into treating a closed auction as still live.
  // Also flip the ended UI immediately when the SERVER's status has
  // moved to a terminal state (sold/ended/expired), because a closed
  // listing must NEVER show "still bidding" copy regardless of time.
  const _serverNowMs = serverTime.nowMs();
  const dataFreshMs = Date.now() - lastServerUpdate;
  const dataIsStale = wsStatus !== "open" && dataFreshMs > 20000;
  const auctionServerEnded = isAuction && listing.auction_ends_at
    && parseServerDate(listing.auction_ends_at).getTime() <= _serverNowMs;
  const outcomeFinalised = !!listing.auction_outcome
    && listing.auction_outcome !== "settling";
  const _LIVE_STATUSES = ["live", "featured", "boosted", "hot"];
  const serverStatusEnded = isAuction && listing.status
    && !_LIVE_STATUSES.includes(listing.status)
    && listing.status !== "scheduled";
  const auctionEnded = isAuction && (
    // Server truth wins: if the listing status has already moved past
    // "live", the auction is ended — no time math needed. Otherwise
    // require server clock past ends_at AND fresh data.
    serverStatusEnded
    || outcomeFinalised
    || (auctionServerEnded && !dataIsStale && !confirmingResult)
  );
  const winnerSettled = outcomeFinalised
    || ["sold", "auction_won", "ended"].includes(listing.status);

  const placeBid = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    const amount = parseFloat(bidAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid bid amount");
      return;
    }
    // v3.99d.341y — open the shared BidConfirmDialog. The dialog's
    // Confirm button labels itself with the exact ``bidPending``
    // amount and POSTs only on confirm — matching the two-touch
    // rule the Forge chip + listing-card quick-bid use.
    setBidPending(+amount.toFixed(2));
    setBidConfirmOpen(true);
  };
  const doPlaceBidConfirmed = async () => {
    if (bidPending == null) return;
    setBidBusy(true);
    try {
      // v3.99d.355 — Send the bidder's browser timezone with each bid so
      // admins can see where hot-auction bidders live and time future
      // relists for their evenings. Best-effort; skipped if unavailable.
      let bidderTz = null;
      try { bidderTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* noop */ }
      await api.post(`/listings/${lid}/bid`, { amount: bidPending, bidder_timezone: bidderTz });
      toast.success(`Bid placed: $${bidPending.toFixed(2)}`);
      setBidAmount("");
      setBidConfirmOpen(false);
      setBidPending(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBidBusy(false);
    }
  };

  const addToCart = async () => {
    if (!user) { nav("/login"); return; }
    try {
      await api.post(`/cart`, { listing_id: lid, quantity: qty });
      toast.success("Added to cart");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // v3.99d.251 — Toggle: adds when not watching, removes when watching.
  // Optimistically flips local state; rolls back on server error.
  const watch = async () => {
    if (!user) { nav("/login"); return; }
    if (watchBusy) return;
    setWatchBusy(true);
    const wasWatching = watching;
    setWatching(!wasWatching);
    try {
      if (wasWatching) {
        await api.delete(`/watchlist/${lid}`);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/watchlist/${lid}`);
        toast.success("Added to watchlist");
      }
    } catch (err) {
      setWatching(wasWatching);  // rollback
      toast.error(formatApiError(err));
    } finally {
      setWatchBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="listing-detail">
      {/* v3.99d.264 — Cross-site promo strip. Skip on the listing's own
          page if THIS listing is the promo (avoid self-advertising). */}
      {!listing?.promotional_product && (
        <PromoStripBanner className="mb-6" testid="detail-promo-strip"/>
      )}
      <div className="grid lg:grid-cols-12 gap-12">
      {/* Gallery */}
      <div className="lg:col-span-7">
        <motion.div className="aspect-square bg-[var(--surface-2)] mb-3 overflow-hidden max-w-[560px]"
                    initial={{opacity:0}} animate={{opacity:1}} transition={{duration:0.5}}>
          <img src={listing.images?.[activeImg]} alt="" className="w-full h-full object-cover"/>
        </motion.div>
        <div className="flex gap-2 max-w-[560px]">
          {listing.images?.map((src, i) => (
            <button key={i} onClick={()=>setActiveImg(i)}
                    className={`w-20 h-20 overflow-hidden border-2 ${i===activeImg?"border-[var(--primary)]":"border-transparent"}`}>
              <img src={src} alt="" className="w-full h-full object-cover"/>
            </button>
          ))}
        </div>
        <div className="mt-10">
          {/* Strain name above description */}
          <div className="label-eyebrow mb-2">Strain</div>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tighter mb-6" style={{color:"var(--text)"}}>
            {listing.title || listing.strain_name}
          </h2>

          <div className="label-eyebrow mb-3">Description</div>
          <p style={{color:"var(--text-2)", fontSize: "15px"}} className="leading-relaxed whitespace-pre-line">{listing.description}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
            <Detail k="Lineage" v={listing.lineage}/>
            <Detail k="Classification" v={listing.classification ? (
              <ClassificationChip value={listing.classification} size="md"
                                  testId={`detail-classification-${listing.id}`}/>
            ) : "—"}/>
            <Detail k="Seed type" v={(listing.seed_type || (listing.category === "regular" ? "regular" : "feminized"))?.toUpperCase()}/>
            <Detail k="Cycle" v={(listing.cycle_type || (listing.category === "autoflower" ? "autoflower" : "photoperiod"))?.toUpperCase()}/>
            <Detail k="Seeds per pack" v={listing.seed_count}/>
            <Detail k="Stock" v={listing.quantity}/>
            <Detail k="Shipping" v={<ShippingHint commerce={commerce}/>}/>
            <Detail k="Vendor" v={listing.store_name}/>
          </div>

          {/* v3.99e — Embedded Strain Atlas (pedigree + trait matrix
              widgets). Renders the same lineage info that lives on
              /lineage/<slug> directly inside the product page so
              shoppers see the strain's provenance + trait profile
              without bouncing away. Falls back to a soft empty state
              if the strain has no public lineage record. */}
          <div className="mt-10" data-testid="listing-strain-atlas-section">
            <div className="label-eyebrow mb-3" style={{color:"var(--gold)"}}>
              Strain Atlas · genetics &amp; traits
            </div>
            <StrainAtlasWidget strainName={listing.strain_name || listing.title}/>
          </div>
        </div>
      </div>

      {/* Sidebar buy/bid */}
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-24">
          <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>
            {(() => {
              const seed = listing.seed_type || (listing.category === "regular" ? "regular" : "feminized");
              const cycle = listing.cycle_type || (listing.category === "autoflower" ? "autoflower" : "photoperiod");
              return `${seed.toUpperCase()} · ${cycle.toUpperCase()}`;
            })()}
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter mb-1" style={{color:"var(--text)"}}>{listing.title || listing.strain_name}</h1>
          <Link to={`/breeder/${listing.vendor_id}`} className="text-sm" style={{color:"var(--text-2)"}}>by <span className="underline" style={{color:"var(--primary-text)"}}>{listing.store_name}</span></Link>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <CollectibleChips listing={listing} size="sm" testId="detail-chips"/>
            {/* v3.99d.262 — One-per-customer pill on detail page. Mirrors
                the card pill so collectors see the limit early. */}
            {listing.one_per_customer && listing.listing_type !== "auction" && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wide border border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/5"
                    data-testid="detail-one-per-customer-pill"
                    title="Promotional item — one per customer (lifetime)">
                <Gift className="w-3 h-3"/>
                1 per customer
              </span>
            )}
            {/* v3.99d.267 — Earn-entries-to-giveaway callouts. Shown when
                this listing is wired to one or more giveaways via the
                purchase-entry feature. Each one is a clickable link to
                the giveaway detail page. */}
            {Array.isArray(listing.entry_to_giveaways)
              && listing.entry_to_giveaways.length > 0
              && listing.listing_type !== "auction"
              && listing.entry_to_giveaways.map((g) => (
                <Link key={g.id}
                      to={`/giveaways/${g.slug || g.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wide border border-[#7FCB99] text-[#7FCB99] bg-[#7FCB99]/5 hover:bg-[#7FCB99]/15 transition-colors"
                      data-testid={`detail-earn-entries-${g.id}`}
                      title={`Buy this → ${g.entries_per_purchase || 5} entries to ${g.title}`}>
                  <Sparkles className="w-3 h-3"/>
                  +{g.entries_per_purchase || 5} entries · {g.title}
                </Link>
              ))}
            {/* v3.99c.69 — Share */}
            <ShareButton
              title={`${listing.title || listing.strain_name} — ${listing.store_name || "CannaForge"}`}
              description={(listing.description || `${listing.title || listing.strain_name} on CannaForge.`).slice(0, 160)}
              path={listingUrl(listing.slug || listing)}
              size="sm"
              testid="listing-detail-share"
            />

            {/* v3.99d.365 — Share to Bidder Banter (auction listings
                only, signed-in collectors only). Drops an auction
                attachment into the /auctions chat. */}
            {(listing.listing_type || "").toLowerCase() === "auction" && user && (
              <ShareToBanterButton listingId={listing.id}/>
            )}
          </div>
          {listing.review_count > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <StarRow value={listing.rating}/>
              <span className="text-sm" style={{color:"var(--text-2)"}}>{listing.rating?.toFixed?.(1)} · {listing.review_count} reviews</span>
            </div>
          )}

          <motion.div key={bumpKey} initial={{scale: bumpKey?1.02:1}} animate={{scale:1}} transition={{duration:0.25}}
                      className="mt-8 p-6 bg-[var(--surface)] border border-[var(--border)]">
            {isOfferOnly ? (
              <>
                {/* v3.99d.300 Phase 1 — Offer Only public surface. */}
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" style={{ color: "var(--gold)" }} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
                        style={{ color: "var(--gold)" }}
                        data-testid="offer-only-eyebrow">
                    {listing.from_house_collection ? "CannaForge House Collection" : "Offer Only"}
                  </span>
                </div>
                <h3 className="font-display text-2xl tracking-tight mb-1"
                    style={{ color: "var(--text)" }}>
                  Make an offer
                </h3>
                <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>
                  This pack is only available through private offers.
                  Submit a price you&apos;re comfortable with — admin will
                  review and accept, decline, or counter (counters arrive
                  in a future release).
                </p>
                {Number(listing.min_offer) > 0 && (
                  <div className="text-[12px] mb-3"
                        style={{ color: "var(--text-muted)" }}
                        data-testid="offer-only-min">
                    Minimum offer: <span className="font-mono"
                      style={{ color: "var(--text)" }}>
                      ${Number(listing.min_offer).toFixed(2)}
                    </span> per unit
                  </div>
                )}
                {Number(listing.suggested_offer) > 0 && (
                  <div className="text-[12px] mb-4"
                        style={{ color: "var(--text-muted)" }}
                        data-testid="offer-only-suggested">
                    Suggested offer: <span className="font-mono"
                      style={{ color: "var(--gold)" }}>
                      ${Number(listing.suggested_offer).toFixed(2)}
                    </span> per unit
                  </div>
                )}

                {myActiveOffer ? (
                  <div className="p-4 border mb-4"
                        style={{
                          background: myActiveOffer.status === "accepted_awaiting_payment"
                            ? "rgba(212,175,55,0.10)"
                            : "rgba(80,168,111,0.08)",
                          borderColor: myActiveOffer.status === "accepted_awaiting_payment"
                            ? "var(--gold-soft, rgba(212,175,55,0.5))"
                            : "var(--border)",
                          borderRadius: 2,
                        }}
                        data-testid="my-active-offer-banner">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
                          style={{ color: myActiveOffer.status === "accepted_awaiting_payment"
                            ? "var(--gold)" : "var(--text-muted)" }}>
                      Your offer · {myActiveOffer.status.replace(/_/g, " ")}
                    </div>
                    <div className="text-sm" style={{ color: "var(--text)" }}>
                      ${Number(myActiveOffer.total_offer).toFixed(2)}{" "}
                      ({myActiveOffer.quantity} × ${Number(myActiveOffer.offer_price_per_unit).toFixed(2)})
                    </div>
                    {myActiveOffer.status === "accepted_awaiting_payment" && (
                      <Link to="/cart"
                            data-testid="my-active-offer-checkout"
                            className="mt-3 inline-flex items-center justify-center w-full px-4 py-2 text-xs font-mono uppercase tracking-wider border"
                            style={{ background: "var(--gold)", color: "#000",
                                      borderColor: "var(--gold)", borderRadius: 2 }}>
                      Complete checkout
                    </Link>
                    )}
                    <Link to="/account?tab=offers"
                           className="mt-2 inline-block text-[11px] font-mono"
                           style={{ color: "var(--text-muted)" }}>
                      View all my offers →
                    </Link>
                  </div>
                ) : (
                  <button onClick={() => {
                            if (!user) { nav("/login"); return; }
                            setOfferOpen(true);
                          }}
                          data-testid="make-offer-btn"
                          className="w-full px-4 py-3 text-sm font-mono uppercase tracking-wider border inline-flex items-center justify-center gap-2"
                          style={{ background: "var(--gold)", color: "#000",
                                    borderColor: "var(--gold)", borderRadius: 2 }}>
                    <Sparkles className="w-4 h-4" />
                    Make an offer
                  </button>
                )}

                {!myActiveOffer && (
                  <button onClick={watch}
                          disabled={watchBusy}
                          className="mt-3 w-full btn-outline flex items-center justify-center gap-2"
                          data-testid="watch-button"
                          aria-pressed={watching}>
                    <Heart className="w-4 h-4"
                            fill={watching ? "currentColor" : "none"}
                            style={watching ? { color: "#E36A5A" } : undefined}/>
                    {watching ? "Watching — remove from watchlist" : "Watch"}
                  </button>
                )}

                <p className="mt-4 text-[11px] leading-relaxed"
                    style={{ color: "var(--text-muted)" }}>
                  Offers are private. Other collectors never see your
                  bid amount. Acceptance triggers a {Number(listing.accepted_offer_payment_hours || 24)}-hour
                  checkout window using your normal cart and payment.
                </p>
              </>
            ) : isAuction ? (
              <>
                <MyBidStatusPill status={myBidStatus} auctionEnded={auctionEnded} />
                <div className="flex justify-between items-start mb-2">
                  <div className="label-eyebrow">
                    {auctionEnded ? "Final Bid" : "Current Bid"}
                  </div>
                  {!auctionEnded && presence > 0 && (
                    <div className="flex items-center gap-1 text-xs" style={{color:"var(--text-muted)"}} data-testid="presence-indicator">
                      <Eye className="w-3 h-3"/>{presence} watching · <Zap className="w-3 h-3" style={{color:"var(--success)"}}/> live
                    </div>
                  )}
                  {auctionEnded && (
                    <div className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 border"
                         style={{ color: "#E36A5A",
                                  borderColor: "#E36A5A88",
                                  background: "rgba(227,106,90,0.10)" }}
                         data-testid="auction-ended-pill">
                      Bids Closed
                    </div>
                  )}
                </div>
                <div className="font-mono text-4xl mb-1" style={{color:"var(--primary-text)"}}>
                  <span data-testid="listing-current-bid">${(listing.current_bid || 0).toFixed(2)}</span>
                </div>
                <div className="text-xs mb-3" style={{color:"var(--text-muted)"}}>
                  {listing.bid_count} bids
                  {!auctionEnded && <> · Min next bid ${minNext}</>}
                  {auctionEnded && winnerSettled && <> · Auction ended</>}
                </div>
                {/* v3.99d.396 / v3.99d.398 — Public inventory pill for
                    auction listings. Only renders when the auction is
                    linked to a buy-now parent (`has_restock_context`).
                    Standalone / vault / one-off auctions get no pill
                    because there is no honest "N left" story to tell
                    — the auction unit IS the sale. */}
                {listing.inventory
                  && listing.inventory.has_restock_context !== false && (
                  <div className="mb-3"
                       data-testid="auction-inventory-pill">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1
                                       border rounded-sm text-[11px]
                                       tracking-wide"
                          style={{
                            borderColor: listing.inventory.is_last_one
                              ? "rgba(255,122,61,0.45)"
                              : "rgba(66,214,107,0.35)",
                            color: listing.inventory.is_last_one
                              ? "#ff9a6b" : "#7ee29a",
                            background: listing.inventory.is_last_one
                              ? "rgba(255,122,61,0.08)"
                              : "rgba(66,214,107,0.08)",
                          }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: listing.inventory.is_last_one
                          ? "#ff7a3d" : "#42d66b",
                      }}/>
                      {listing.inventory.display}
                    </span>
                  </div>
                )}
                {/* v3.99d.376 / v3.99d.379 — Admin-only inline tool to
                    correct a mistyped top bid (e.g. $211 → $21) while
                    keeping the current top bidder in the lead.
                    Rendered whenever the viewer is admin; disabled
                    with a clear reason when there are no bids yet. */}
                {user?.role === "admin" && (
                  <AdminCorrectBidButton
                    listingId={listing.id}
                    currentBid={listing.current_bid}
                    hasBids={(listing.bid_count || 0) > 0}
                    onCorrected={(next) => setListing(prev => prev ? {
                      ...prev, current_bid: next,
                    } : prev)}
                  />
                )}
                {/* v3.99d.249 — Auction wins are excluded from every
                    promotion / coupon / sitewide discount. Surface
                    this on the auction page so bidders never expect
                    a discount to apply later. */}
                <div className="mb-4 text-[10px] leading-snug"
                     style={{ color: "var(--text-muted)" }}
                     data-testid="auction-promo-exclusion-note">
                  Auction wins are excluded from promo codes and sitewide
                  discounts. The winning bid is the final price.
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4" style={{color: auctionEnded ? "var(--text-muted)" : "var(--secondary)"}}/>
                  {auctionEnded ? (
                    <span className="text-sm" style={{color:"var(--text-muted)"}}>
                      Ended {parseServerDate(listing.auction_ends_at).toLocaleString()}
                    </span>
                  ) : (
                    <CountdownTimer endsAt={listing.auction_ends_at}
                                     nowMs={serverTime.nowMs}
                                     onZero={onCountdownZero}/>
                  )}
                </div>
                {/* v3.99d.355 — Local + UTC close-time banner so
                    collectors see EXACTLY when the auction ends in
                    their timezone. Hidden after the auction closes. */}
                {!auctionEnded && (
                  <LocalCloseTimeBanner endsAt={listing.auction_ends_at} />
                )}
                {/* v3.99d.351 — Live-updates disconnect banner. Shown
                    only when the WebSocket is not open AND we haven't
                    had a fresh listing GET in the last few seconds.
                    Deliberately subtle so it never dominates the
                    layout; polling fallback is already recovering the
                    data in the background. */}
                {wsStatus !== "open" && (
                  <div className="mb-3 text-[11px] flex items-center gap-1.5"
                       style={{color: "var(--text-muted)"}}
                       data-testid="auction-live-disconnected-banner">
                    <span className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{background: "var(--text-muted)"}} />
                    Live updates disconnected — refreshing automatically
                  </div>
                )}
                {auctionEnded ? (
                  /* v3.99c.137 — Greyed-out post-auction state. No
                     interactive form so the auction can't be acted on
                     after close. */
                  <div className="p-4 border text-center"
                       style={{
                         background: "rgba(80,80,80,0.08)",
                         borderColor: "var(--border)",
                         opacity: 0.85,
                       }}
                       data-testid="auction-closed-block">
                    <div className="text-xs uppercase tracking-[0.18em] mb-1"
                         style={{ color: "var(--text-muted)" }}>
                      Bidding closed
                    </div>
                    <div className="text-sm" style={{ color: "var(--text-2)" }}>
                      {listing.bid_count > 0
                        ? "The winning bidder is being notified to complete payment."
                        : "Auction closed with no bids."}
                    </div>
                    {/* v3.99d.272 — If the viewing user is the winner
                        and hasn't paid yet, surface the inline Pay Now
                        CTA right here so they don't have to bounce to
                        /my/bids to settle. */}
                    {myBidStatus?.status === "payment_required" && (
                      <div className="mt-3 flex justify-center" data-testid="auction-winner-pay-cta">
                        <AuctionPayButton listingId={lid} />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                  <ShippingReassuranceLine commerce={commerce} />
                  <FreeShippingProgress cart={cart} />
                  <form onSubmit={placeBid} className="flex gap-2">
                    <input type="number" step="0.01" min={minNext} required
                           value={bidAmount} onChange={(e)=>setBidAmount(e.target.value)}
                           placeholder={`$${minNext} or more`} className="input-field font-mono"
                           disabled={preview_mode}
                           data-testid="bid-amount-input"/>
                    {preview_mode ? (
                      <button type="button" onClick={watch}
                              className="btn-primary inline-flex items-center gap-2"
                              data-testid="preview-notify-bidding">
                        <BellRing className="w-4 h-4"/>Notify me when bidding opens
                      </button>
                    ) : (
                      <button type="submit" className="btn-primary" data-testid="place-bid-button">
                        <Gavel className="w-4 h-4"/>Place bid
                      </button>
                    )}
                  </form>
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }} data-testid="auction-private-bidding-blurb">
                    Private bidding · Other users only see your CannaForge username. Your real name and account information remain private.
                  </div>
                  </>
                )}
                {preview_mode && !auctionEnded && (
                  <div className="mt-2 text-[11px]" style={{color:"var(--text-muted)"}}>
                    Preview Auction · Watch this listing to be alerted the moment bidding opens.
                  </div>
                )}
                {!auctionEnded && (
                  <button onClick={watch}
                          disabled={watchBusy}
                          className="mt-3 w-full btn-outline flex items-center justify-center gap-2"
                          data-testid="watch-button"
                          aria-pressed={watching}>
                    <Heart className="w-4 h-4"
                           fill={watching ? "currentColor" : "none"}
                           style={watching ? { color: "#E36A5A" } : undefined}/>
                    {watching
                      ? "Watching — remove from watchlist"
                      : "Watch Auction"}
                  </button>
                )}
                {/* v3.99d.251 — Anti-snipe transparency. The 2-min
                    extension lives at the bid endpoint already; surface
                    the rule so bidders know the timer is fair. */}
                {!auctionEnded && (
                  <div className="mt-3 text-[10px] leading-snug"
                       style={{ color: "var(--text-muted)" }}
                       data-testid="auction-antisnipe-note">
                    Last-minute bids may extend the timer to keep auctions fair.
                  </div>
                )}

                {/* v3.99d.380 / v3.99d.381 — Bid history is visible to
                    everyone; usernames are first-letter + stars for
                    the public, unredacted for admins. */}
                {bids.length > 0 && (
                  <div className="mt-6 border-t border-[var(--border)] pt-4"
                       data-testid="bid-history-card">
                    <div className="label-eyebrow mb-3 flex items-center gap-2">
                      {user?.role === "admin" && (
                        <span style={{ background: "var(--gold)",
                                         color: "var(--bg)",
                                         padding: "1px 5px",
                                         borderRadius: 2, fontSize: 9,
                                         letterSpacing: "0.14em" }}>
                          ADMIN
                        </span>
                      )}
                      Bid history
                    </div>
                    <ul className="space-y-2 text-sm font-mono max-h-48 overflow-auto" data-testid="bid-history">
                      {bids.map(b => (
                        <li key={b.id} className="flex justify-between">
                          <span>
                            {user?.role === "admin"
                              ? (b.user_name || "—")
                              : `${b.user_name?.[0] || ""}${"*".repeat(Math.max(0, (b.user_name?.length || 1) - 1))}`}
                          </span>
                          <span style={{color:"var(--primary-text)"}}>${b.amount.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="label-eyebrow mb-2">Price</div>
                {listing.sale_price != null && listing.sale_price < listing.price ? (
                  <>
                    <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                      <span className="font-mono text-4xl" style={{color:"var(--gold)"}}>
                        ${listing.sale_price.toFixed(2)}
                      </span>
                      <span className="font-mono text-xl line-through" style={{color:"var(--text-muted)"}}>
                        ${listing.price.toFixed(2)}
                      </span>
                    </div>
                    <div className="mb-5">
                      <span
                        className="inline-block text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1"
                        style={{
                          background: "rgba(212,175,55,0.15)",
                          color: "var(--gold)",
                          border: "1px solid rgba(212,175,55,0.5)",
                          borderRadius: 2,
                        }}
                        data-testid="sale-pill-detail"
                      >
                        {Math.round(((listing.price - listing.sale_price) / listing.price) * 100)}% OFF
                        {listing.applied_promotion?.name && ` · ${listing.applied_promotion.name}`}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="font-mono text-4xl mb-5" style={{color:"var(--primary-text)"}}>${listing.price.toFixed(2)}</div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm" style={{color:"var(--text-2)"}}>Quantity</span>
                  <div className="flex border border-[var(--border)]">
                    <button onClick={()=>setQty(Math.max(1, qty-1))} className="px-3 py-2" data-testid="qty-decrease">-</button>
                    <span className="px-4 py-2 font-mono" data-testid="qty-display">{qty}</span>
                    <button onClick={()=>setQty(Math.min(listing.quantity, qty+1))} className="px-3 py-2" data-testid="qty-increase">+</button>
                  </div>
                  <span className="text-xs" style={{color:"var(--text-muted)"}}>{listing.quantity} available</span>
                </div>
                {/* v3.99d.396 — Public inventory pill for buy-now
                    listings. Shows the honest, computed
                    available_now count (master quantity minus units
                    reserved for auctions / open orders). Live-updates
                    on every detail reload. */}
                {listing.inventory && (
                  <div className="mb-4 flex items-center gap-2"
                       data-testid="listing-inventory-pill">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1
                                       border rounded-sm text-[11px] font-mono
                                       uppercase tracking-widest"
                          style={{
                            borderColor: listing.inventory.available_now > 0
                              ? "rgba(66,214,107,0.35)"
                              : "rgba(255,122,61,0.35)",
                            color: listing.inventory.available_now > 0
                              ? "#7ee29a" : "#ff9a6b",
                            background: listing.inventory.available_now > 0
                              ? "rgba(66,214,107,0.08)"
                              : "rgba(255,122,61,0.08)",
                          }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: listing.inventory.available_now > 0
                          ? "#42d66b" : "#ff7a3d",
                      }}/>
                      {listing.inventory.display}
                    </span>
                    {listing.inventory.at_auction > 0 && (
                      <span className="text-[11px]"
                            style={{color:"var(--text-muted)"}}>
                        · {listing.inventory.at_auction} currently at auction
                      </span>
                    )}
                  </div>
                )}
                <ShippingReassuranceLine commerce={commerce} />
                <FreeShippingProgress cart={cart} />
                {preview_mode ? (
                  <>
                    <button onClick={watch}
                            className="w-full btn-primary justify-center inline-flex items-center gap-2"
                            data-testid="preview-notify-buy">
                      <BellRing className="w-4 h-4"/>Notify me when buying opens
                    </button>
                    <div className="mt-2 text-[11px] text-center" style={{color:"var(--text-muted)"}}>
                      Coming soon · Currently in preview access
                    </div>
                  </>
                ) : (
                  <button onClick={addToCart} disabled={listing.quantity===0}
                          className="w-full btn-primary justify-center" data-testid="add-to-cart">
                    <ShoppingCart className="w-4 h-4"/>{listing.quantity===0 ? "Sold out" : "Add to cart"}
                  </button>
                )}
                <button onClick={watch} disabled={watchBusy}
                        className="mt-3 w-full btn-outline flex items-center justify-center gap-2"
                        data-testid="watch-button"
                        aria-pressed={watching}>
                  <Heart className="w-4 h-4"
                         fill={watching ? "currentColor" : "none"}
                         style={watching ? { color: "#E36A5A" } : undefined}/>
                  {watching ? "On your watchlist — remove" : "Add to watchlist"}
                </button>
              </>
            )}
          </motion.div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-xs" style={{color:"var(--text-2)"}}>
            <Trust icon={Shield} label="Verified seller"/>
            <Trust icon={Truck} label="Discreet shipping"/>
            <Trust icon={MapPin} label="Worldwide"/>
          </div>
        </div>
      </div>
      </div>

      <div className="mt-16 max-w-3xl">
        <ReviewSection listingId={id} strainName={listing.strain_name || listing.title}/>
      </div>

      {/* v3.99d.300 Phase 1 — Make Offer modal. Mounted at root so it
          overlays the entire detail page. */}
      {offerOpen && (
        <MakeOfferModal listing={listing}
                         onClose={() => setOfferOpen(false)}
                         onSubmitted={(offer) => setMyActiveOffer(offer)} />
      )}
      {/* v3.99d.341y — shared 2-touch bid confirmation. */}
      <BidConfirmDialog
        open={bidConfirmOpen}
        amount={bidPending}
        title={listing.title || listing.strain_name}
        busy={bidBusy}
        onCancel={() => { if (!bidBusy) { setBidConfirmOpen(false); setBidPending(null); } }}
        onConfirm={doPlaceBidConfirmed}
        testidRoot="listing-detail-bid-confirm"
      />
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="label-eyebrow text-[10px] mb-1">{k}</div>
      <div className="text-base font-medium" style={{color:"var(--text)"}}>{v ?? "—"}</div>
    </div>
  );
}

// v3.99d.232 — Replaces the old per-pack "$0.00 shipping" display.
// Reads live commerce settings so the copy stays in sync with what
// the cart actually charges (flat rate, free over threshold). Falls
// back gracefully when the settings call hasn't resolved yet.
function ShippingHint({ commerce }) {
  const flat = Number(commerce?.shipping_flat_rate ?? 6);
  const threshold = Number(commerce?.free_shipping_threshold ?? 100);
  const freeOn = commerce?.free_shipping_enabled !== false;
  const flatStr = `$${flat.toFixed(flat % 1 === 0 ? 0 : 2)}`;
  const threshStr = `$${threshold.toFixed(threshold % 1 === 0 ? 0 : 2)}`;
  return (
    <div className="text-[13px] leading-snug" data-testid="listing-shipping-hint">
      <span style={{ color: "var(--text)" }}>Calculated at checkout</span>
      <span className="block text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
        Never more than {flatStr}/cart
        {freeOn && threshold > 0 ? ` · Free over ${threshStr}` : ""}
      </span>
    </div>
  );
}

// v3.99d.233 — Compact inline reassurance line for placement directly
// above the Bid / Add-to-cart buttons. Same rule, StockX-style spot —
// reassures multi-item shoppers that shipping never compounds.
// v3.99d.234 — Mini free-shipping progress bar. Renders only when
// the shopper has cart items AND has not yet hit the threshold.
// Reuses the cart totals payload (free_shipping_threshold,
// free_shipping_remaining, free_shipping_applied, subtotal) so it
// stays in lock-step with the cart page's identical widget.
function FreeShippingProgress({ cart }) {
  if (!cart) return null;
  const subtotal = Number(cart.subtotal || 0);
  if (subtotal <= 0) return null;
  const threshold = Number(cart.free_shipping_threshold || 0);
  if (threshold <= 0) return null;
  const applied = !!cart.free_shipping_applied;
  const remaining = Number(cart.free_shipping_remaining || 0);
  const pct = Math.min(100, (subtotal / threshold) * 100);
  return (
    <div
      data-testid="listing-free-ship-progress"
      className="mb-3 -mt-2"
    >
      <div className="flex items-center justify-between mb-1 text-[11px]">
        <span style={{ color: applied ? "var(--primary)" : "var(--text-2)" }}>
          {applied
            ? "Free shipping unlocked in your cart 🎉"
            : <>Add <strong style={{ color: "var(--text)" }}>${remaining.toFixed(2)}</strong> more to your cart for free shipping</>}
        </span>
        <span className="font-mono" style={{ color: "var(--text-muted)" }}>
          ${subtotal.toFixed(2)} / ${threshold.toFixed(0)}
        </span>
      </div>
      <div className="h-1 bg-[var(--surface-2)] rounded-sm overflow-hidden">
        <div
          className="h-full"
          style={{
            width: `${pct}%`,
            background: applied ? "var(--primary)" : "var(--gold)",
            transition: "width 240ms ease-out",
          }}
        />
      </div>
    </div>
  );
}

function ShippingReassuranceLine({ commerce }) {
  const flat = Number(commerce?.shipping_flat_rate ?? 6);
  const threshold = Number(commerce?.free_shipping_threshold ?? 100);
  const freeOn = commerce?.free_shipping_enabled !== false;
  const flatStr = `$${flat.toFixed(flat % 1 === 0 ? 0 : 2)}`;
  const threshStr = `$${threshold.toFixed(threshold % 1 === 0 ? 0 : 2)}`;
  return (
    <div
      data-testid="shipping-reassurance"
      className="mb-3 flex items-center gap-2 text-[12px] border-l-2 pl-2 py-1"
      style={{
        borderColor: "var(--gold)",
        background: "rgba(212,175,55,0.06)",
        color: "var(--text-2)",
      }}
    >
      <Truck className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--gold)" }} />
      <span>
        Shipping calculated at checkout · <strong style={{ color: "var(--text)" }}>never more than {flatStr}/cart</strong>
        {freeOn && threshold > 0 ? (
          <>, <strong style={{ color: "var(--text)" }}>free over {threshStr}</strong></>
        ) : null}.
      </span>
    </div>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-3 text-center">
      <Icon className="w-4 h-4 mx-auto mb-1" style={{color:"var(--primary-text)"}}/>
      <div className="text-[10px] font-semibold uppercase tracking-wider">{label}</div>
    </div>
  );
}

// v3.99d.272 — Personal bidder status pill for the auction sidebar.
// Renders nothing when the user has never bid (per spec). Uses the
// authoritative `status` field from /listings/{id}/my-bid-status so
// it can never disagree with the outbid email or /my/bids dashboard.
function MyBidStatusPill({ status, auctionEnded }) {
  if (!status || status.status === "not_bid" || !status.has_bid) return null;
  const s = status.status;
  const myTop = Number(status.my_top_bid || 0);
  const map = {
    winning:          { label: "You are the highest bidder", sub: "Your current bid is leading.",          Icon: Trophy,        color: "#4FA86F", bg: "rgba(79,168,111,0.14)", border: "rgba(79,168,111,0.55)" },
    outbid:           { label: "You have been outbid",        sub: "Place a new bid to retake the lead.",   Icon: AlertCircle,   color: "#E36A5A", bg: "rgba(227,106,90,0.12)", border: "rgba(227,106,90,0.55)" },
    won:              { label: "You won this auction",         sub: "Congratulations — payment complete.",   Icon: Trophy,        color: "#D4AF37", bg: "rgba(212,175,55,0.16)", border: "rgba(212,175,55,0.55)" },
    payment_required: { label: "You won this auction",         sub: "Payment required — complete it below.", Icon: Trophy,        color: "#D4AF37", bg: "rgba(212,175,55,0.16)", border: "rgba(212,175,55,0.55)" },
    lost:             { label: "You did not win this auction", sub: "Another bidder placed a higher bid.",   Icon: AlertCircle,   color: "#A8AEB8", bg: "rgba(168,174,184,0.10)", border: "rgba(168,174,184,0.45)" },
    ended_no_bids:    { label: "Auction ended",                sub: "Reserve not met.",                       Icon: AlertCircle,   color: "#A8AEB8", bg: "rgba(168,174,184,0.10)", border: "rgba(168,174,184,0.45)" },
  }[s];
  if (!map) return null;
  const { label, sub, Icon, color, bg, border } = map;
  return (
    <div
      className="mb-4 px-3 py-2.5 border flex items-start gap-2.5"
      style={{ background: bg, borderColor: border, borderRadius: 2 }}
      data-testid={`my-bid-status-pill-${s}`}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono uppercase tracking-[0.18em]" style={{ color }}>
          {label}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "var(--text-2)" }}>
          {sub}{myTop > 0 && <> · Your top bid <span className="font-mono" style={{ color: "var(--text)" }}>${myTop.toFixed(2)}</span></>}
        </div>
      </div>
    </div>
  );
}


/**
 * ShareToBanterButton — v3.99d.365
 * Compact button that shares this auction as an attachment into
 * Bidder Banter with an optional short note.
 */
function ShareToBanterButton({ listingId }) {
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [posting, setPosting] = React.useState(false);
  // v3.99d.372 — Only render if the platform toggle has Bidder Banter
  // enabled. If admin has paused Banter we hide the button entirely so
  // signed-in collectors don't get a share modal that will 503 on
  // submit.
  const [banterEnabled, setBanterEnabled] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    api.get("/auctions-hub/config")
      .then((r) => { if (!cancelled) setBanterEnabled(!!r.data?.banter_enabled); })
      .catch(() => { if (!cancelled) setBanterEnabled(false); });
    return () => { cancelled = true; };
  }, []);
  if (banterEnabled === false) return null;
  const submit = async () => {
    setPosting(true);
    try {
      await api.post("/auctions-hub/banter/share-auction",
                     { listing_id: listingId, text: note.trim() });
      toast.success("Shared to Bidder Banter");
      setOpen(false);
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err) || "Share failed");
    } finally {
      setPosting(false);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="listing-detail-share-to-banter"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider border transition hover:opacity-80"
        style={{ borderColor: "var(--gold)",
                  color: "var(--gold)",
                  borderRadius: 2 }}
        title="Share this auction into Bidder Banter">
        <MessageCircle className="w-3 h-3"/>
        Share to Banter
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
              data-testid="share-to-banter-modal">
          <div className="absolute inset-0"
                onClick={() => !posting && setOpen(false)}
                style={{ background: "rgba(8,9,11,0.85)",
                          backdropFilter: "blur(4px)" }}/>
          <div className="relative w-full max-w-md border p-5"
                style={{ background: "var(--surface)",
                          borderColor: "var(--gold)",
                          borderRadius: 2 }}>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                  style={{ color: "var(--gold)" }}>
              Share to Bidder Banter
            </div>
            <p className="text-sm mb-3"
                style={{ color: "var(--text-2)" }}>
              Drop this auction into the chat with an optional short note.
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note (optional)"
              maxLength={200}
              rows={3}
              className="w-full px-3 py-2 text-sm border mb-3"
              style={{ borderColor: "var(--border)",
                        background: "var(--card)",
                        color: "var(--text)",
                        borderRadius: 2 }}
              data-testid="share-to-banter-note"/>
            <div className="flex justify-end gap-2">
              <button type="button"
                       onClick={() => setOpen(false)}
                       disabled={posting}
                       className="btn-secondary"
                       data-testid="share-to-banter-cancel">
                Cancel
              </button>
              <button type="button"
                       onClick={submit}
                       disabled={posting}
                       className="btn-primary"
                       data-testid="share-to-banter-submit">
                {posting ? "Sharing…" : "Share"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/**
 * AdminCorrectBidButton — v3.99d.376
 *
 * Admin-only inline pencil that lets ops rewrite the current top bid
 * without changing who the top bidder is. Renders only when the caller
 * is an admin *and* the auction already has a top bidder — no point
 * before the first bid lands. Backend validates the new amount against
 * starting_bid and the runner-up bid so the current top bidder still
 * legitimately holds the lead.
 */
function AdminCorrectBidButton({ listingId, currentBid, hasBids, onCorrected }) {
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const start = () => {
    setAmount(String((currentBid || 0).toFixed(2)));
    setReason("");
    setOpen(true);
  };

  const submit = async () => {
    const num = parseFloat(amount);
    if (!Number.isFinite(num) || num <= 0) {
      toast.error("Enter a positive dollar amount.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/auctions/${listingId}/correct-top-bid`,
        { new_amount: num, reason: reason.trim() || undefined });
      const { from_amount, to_amount } = r.data || {};
      toast.success(
        `Corrected top bid: $${(from_amount ?? 0).toFixed(2)} → ` +
        `$${(to_amount ?? num).toFixed(2)} (same bidder still leading).`
      );
      if (onCorrected) onCorrected(to_amount ?? num);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* v3.99d.379 — Big, unmissable admin toolbar row. Sits under
          the current bid + bid-count line so ops can spot the tool
          without hunting. Hidden entirely from customers. */}
      <div className="mb-5 border px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--gold)",
                     background: "rgba(212,175,55,0.06)",
                     borderRadius: 3 }}
           data-testid="admin-bid-tools-bar">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] flex items-center gap-2"
             style={{ color: "var(--gold)" }}>
          <span style={{ background: "var(--gold)", color: "var(--bg)",
                          padding: "1px 5px", borderRadius: 2 }}>
            ADMIN
          </span>
          Bid controls
        </div>
        <button type="button" onClick={start}
                disabled={!hasBids}
                title={hasBids
                  ? "Rewrite the current top bid while keeping the same bidder in the lead"
                  : "This auction has no bids yet — nothing to correct."}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "var(--gold)", color: "var(--bg)",
                          borderRadius: 2, fontWeight: 700 }}
                data-testid="admin-correct-top-bid-btn">
          <PencilLine className="w-3 h-3"/>
          Edit current bid
        </button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
             style={{ background: "rgba(0,0,0,0.6)" }}
             onClick={() => !busy && setOpen(false)}>
          <div className="w-full max-w-md border p-5"
               style={{ background: "var(--surface)",
                         borderColor: "var(--border)", borderRadius: 4 }}
               onClick={(e) => e.stopPropagation()}
               data-testid="admin-correct-top-bid-modal">
            <h3 className="text-sm font-mono uppercase tracking-[0.18em] mb-3"
                style={{ color: "var(--gold)" }}>
              Correct top bid
            </h3>
            <p className="text-[12px] leading-relaxed mb-4"
               style={{ color: "var(--text-2)" }}>
              Rewrites the current top bid amount but keeps the same
              collector in the lead. The new value must be at least the
              starting bid and greater than the runner-up bid so the top
              bidder legitimately holds the lead.
            </p>
            <label className="text-[10px] font-mono uppercase tracking-wider"
                   style={{ color: "var(--text-muted)" }}>
              New bid amount (USD)
            </label>
            <input type="number" step="0.01" min="0" value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   disabled={busy}
                   className="w-full mt-1 mb-3 px-3 py-2 text-sm border"
                   style={{ background: "var(--card)",
                             borderColor: "var(--border)",
                             color: "var(--text)", borderRadius: 2 }}
                   data-testid="admin-correct-top-bid-amount"/>
            <label className="text-[10px] font-mono uppercase tracking-wider"
                   style={{ color: "var(--text-muted)" }}>
              Reason (optional — logged for audit)
            </label>
            <textarea value={reason}
                       onChange={(e) => setReason(e.target.value)}
                       maxLength={500} rows={2}
                       placeholder="e.g. bidder DM confirmed typo $211 → $21"
                       disabled={busy}
                       className="w-full mt-1 mb-4 px-3 py-2 text-sm border"
                       style={{ background: "var(--card)",
                                 borderColor: "var(--border)",
                                 color: "var(--text)", borderRadius: 2 }}
                       data-testid="admin-correct-top-bid-reason"/>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)}
                      disabled={busy}
                      className="btn-secondary"
                      data-testid="admin-correct-top-bid-cancel">
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy}
                      className="btn-primary"
                      data-testid="admin-correct-top-bid-submit">
                {busy ? "Correcting…" : "Save correction"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
